/** Import-facing comment components (RFC 0324 3b). */

import { createElement, useLayoutEffect, useRef } from 'react'
import {
  type CommentChipElement,
  type CommentElementAuthor,
  type CommentElementComment,
  type CommentPort,
  type CommentRowElement,
  type CommentThreadElement,
  type CommentThreadOptions,
  ensureCommentElements,
} from './commentElements'

ensureCommentElements()

export interface CommentRowProps {
  comment: CommentElementComment
  author: CommentElementAuthor
  now?: number
  className?: string
}

/** One comment with its writer's face: the same element in every surface. */
export function CommentRow({
  comment,
  author,
  now,
  className,
}: CommentRowProps) {
  const ref = useRef<CommentRowElement>(null)
  useLayoutEffect(() => {
    const element = ref.current
    if (!element) return
    element.comment = comment
    element.author = author
    element.now = now
  }, [comment, author, now])
  return createElement('mod-comment-row', { ref, className })
}

export interface CommentThreadProps {
  port: CommentPort
  target: Record<string, string>
  showResolved?: boolean
  /** What this app's thread can do (RFC 0324 3h). */
  options?: CommentThreadOptions
  className?: string
}

/** Every exchange on a target, drawn by the host's element (RFC 0324 3b). */
export function CommentThread({
  port,
  target,
  showResolved,
  options,
  className,
}: CommentThreadProps) {
  const ref = useRef<CommentThreadElement>(null)
  useLayoutEffect(() => {
    const element = ref.current
    if (!element) return
    if (options) element.options = options
    element.port = port
    element.target = target
    element.showResolved = Boolean(showResolved)
  }, [port, target, showResolved, options])
  return createElement('mod-comment-thread', { ref, className })
}

export interface CommentChipProps {
  port: CommentPort
  target: Record<string, string>
  /** Opening the threads is the app's to decide: a popover, the column. */
  onOpen?: (threadIds: string[]) => void
  className?: string
}

/** The count on a passage or a shape, with the writers' faces (RFC 0324 3b). */
export function CommentChip({
  port,
  target,
  onOpen,
  className,
}: CommentChipProps) {
  const ref = useRef<CommentChipElement>(null)
  useLayoutEffect(() => {
    const element = ref.current
    if (!element) return
    element.port = port
    element.target = target
  }, [port, target])
  useLayoutEffect(() => {
    const element = ref.current
    if (!element || !onOpen) return
    const listener = (e: Event) =>
      onOpen((e as CustomEvent<{ threadIds: string[] }>).detail.threadIds)
    element.addEventListener('mod-comment-open', listener)
    return () => element.removeEventListener('mod-comment-open', listener)
  }, [onOpen])
  return createElement('mod-comment-chip', { ref, className })
}
